import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import MCQLogic from "../../SubtopicsPage/MCQLogic";
import { CodeBlock } from "../../CodeOutputBlocks";

const questionsData = [
  {
    question: (
      <div>
        <p>What is printed?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def __init__(self, name, price):
        self.name = name
        self.price = price

p = Product("Shoes", 500)
print(p.name)`}
        />
      </div>
    ),
    options: ["Shoes", "500", "Product", "Error"],
    answer: "Shoes",
  },

  {
    question: (
      <div>
        <p>What will be the output?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def __init__(self, name, price):
        self.name = name
        self.price = price

p = Product("Shoes", 500)
print(p.price)`}
        />
      </div>
    ),
    options: ["Shoes", "500", "name", "Error"],
    answer: "500",
  },

  {
    question: (
      <div>
        <p>What does this method do?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def apply_discount(self):
        self.price = self.price - 50

p = Product("Bag", 500)
p.apply_discount()
print(p.price)`}
        />
      </div>
    ),
    options: ["550", "450", "500", "Error"],
    answer: "450",
  },

  {
    question: (
      <div>
        <p>How do you correctly call a method?</p>
        <CodeBlock
          language="python"
          code={`p = Product("Watch", 1000)
p.apply_discount()`}
        />
      </div>
    ),
    options: [
      "Product.apply_discount()",
      "p.apply_discount()",
      "apply_discount(p)",
      "self.apply_discount()",
    ],
    answer: "p.apply_discount()",
  },

  {
    question: (
      <div>
        <p>What is printed?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def __init__(self, name):
        self.name = name
    
    def show(self):
        print("Product:", self.name)

p = Product("Shoes")
p.show()`}
        />
      </div>
    ),
    options: [
      "Product: Shoes",
      "Shoes",
      "self.name",
      "Error",
    ],
    answer: "Product: Shoes",
  },

  {
    question: (
      <div>
        <p>Which line defines an attribute?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def __init__(self):
        self.rating = 4.5`}
        />
      </div>
    ),
    options: [
      "def __init__(self):",
      "self.rating = 4.5",
      "class Product:",
      "print(rating)",
    ],
    answer: "self.rating = 4.5",
  },

  {
    question: (
      <div>
        <p>What is the role of <b>self</b> in this method?</p>
        <CodeBlock
          language="python"
          code={`def get_price(self):
    return self.price`}
        />
      </div>
    ),
    options: [
      "Refers to the class",
      "Refers to the current object",
      "Refers to all objects",
      "Not needed",
    ],
    answer: "Refers to the current object",
  },

  {
    question: (
      <div>
        <p>What will be printed?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def __init__(self, name, price, rating):
        self.name = name
        self.price = price
        self.rating = rating
    
    def info(self):
        print(f"Product: {self.name}, Price: {self.price}, Rating: {self.rating}")

p = Product("Shoes", 450, 4.5)
p.info()`}
        />
      </div>
    ),
    options: [
      "Product: Shoes, Price: 450, Rating: 4.5",
      "Shoes 450 4.5",
      "Error",
      "None",
    ],
    answer: "Product: Shoes, Price: 450, Rating: 4.5",
  },

  {
    question: (
      <div>
        <p>Which of these is a method?</p>
        <CodeBlock
          language="python"
          code={`def apply_discount(self):
    self.price -= 50`}
        />
      </div>
    ),
    options: [
      "self.price",
      "apply_discount",
      "class Product",
      "self",
    ],
    answer: "apply_discount",
  },

  {
    question: (
      <div>
        <p>Can a method change an object's attribute?</p>
        <CodeBlock
          language="python"
          code={`class Product:
    def set_price(self, new_price):
        self.price = new_price

p = Product("Book", 300)
p.set_price(250)
print(p.price)`}
        />
      </div>
    ),
    options: ["300", "250", "Error", "None"],
    answer: "250",
  },

  {
    question: "What are attributes in a class?",
    options: [
      "Functions that do actions",
      "Data/variables that store object state",
      "Loops inside class",
      "Imported modules",
    ],
    answer: "Data/variables that store object state",
  },

  {
    question: "What are methods in a class?",
    options: [
      "Variables that hold values",
      "Functions defined inside a class that define behavior",
      "Class names",
      "Comments",
    ],
    answer: "Functions defined inside a class that define behavior",
  },

  {
    question: "How do you define an instance attribute in __init__?",
    options: [
      "price = 500",
      "self.price = 500",
      "Product.price = 500",
      "def price = 500",
    ],
    answer: "self.price = 500",
  },

  {
    question: "What does this combination represent?",
    options: [
      "Only data, no actions",
      "Only methods, no data",
      "Data (attributes) + Behavior (methods) together",
      "Separate functions and variables",
    ],
    answer: "Data (attributes) + Behavior (methods) together",
  },

  {
    question: "Why do we use 'self' in methods?",
    options: [
      "It's optional",
      "To refer to the current object and access its attributes/methods",
      "To refer to the class",
      "To create new objects",
    ],
    answer:
      "To refer to the current object and access its attributes/methods",
  },
];


const Attributes_Methods_MCQ = ({
  subtopicId,
  goalName,
  courseName,
  onComplete
}) => {
  const { markSubtopicComplete, loadProgressSummary, completedContent } = useAuth();

  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const randomQuestions = [...questionsData].sort(() => Math.random() - 0.5);

  // Check if subtopic is already completed
  useEffect(() => {
    if (subtopicId && completedContent.includes(subtopicId)) {
      setIsCompleted(true);
    }
  }, [completedContent, subtopicId]);

  const handleCompletion = async () => {
    if (isLoading || isCompleted) return;

    try {
      setIsLoading(true);

      // Validate that we have the required parameters
      if (!subtopicId) {
        console.error("❌ Subtopic ID is required");
        alert("Error: Subtopic ID is missing");
        return;
      }

      console.log("🎯 Marking subtopic complete:", {
        subtopicId,
        goalName,
        courseName
      });

      const result = await markSubtopicComplete(
        subtopicId,
        goalName || "Goal 1",
        courseName || "Static Website: HTML CSS & Bootstrap"
      );

      if (result.success) {
        await loadProgressSummary();
        setIsCompleted(true);
        console.log("✅ MCQ successfully marked as completed");

        // Call the parent completion handler if provided
        if (onComplete) {
          onComplete();
        }
      } else {
        console.error("❌ Failed to mark MCQ complete:", result.message);
        alert(`Failed to mark as complete: ${result.message}`);
      }
    } catch (error) {
      console.error("❌ Failed to mark MCQ complete:", error);
      alert("Failed to mark as complete. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <MCQLogic
      title="Attribute Methods | MCQs"
      questions={randomQuestions}
      isCompleted={isCompleted}
      isLoading={isLoading}
      onComplete={handleCompletion}
      subtopicId={subtopicId}
      goalName={goalName}
      courseName={courseName}
    />
  );
};

export default Attributes_Methods_MCQ;
